import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
    ArrowRight,
    FileText,
    Users,
    BarChart,
    PlayCircle,
    Menu,
    X,
    ShieldCheck,
    Lock,
    Zap
} from 'lucide-react';

const LandingPage = () => {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-[#030712] text-slate-50 font-sans selection:bg-primary/30 overflow-x-hidden">
            {/* Background Effects */}
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/20 blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-600/20 blur-[120px]" />
                <div className="absolute top-[40%] left-[60%] w-[30%] h-[30%] rounded-full bg-purple-600/10 blur-[100px]" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5 mix-blend-overlay"></div>
            </div>

            {/* Navigation Bar */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#030712]/80 backdrop-blur-xl border-b border-white/5 py-4' : 'bg-transparent py-6'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center">
                        {/* Logo */}
                        <div className="flex-shrink-0 flex items-center gap-3 cursor-pointer group" onClick={() => window.scrollTo(0, 0)}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#6366f1" className="w-10 h-10 transition-transform group-hover:scale-110">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                            </svg>
                            <span className="font-bold text-xl tracking-tight text-white hidden sm:block">Invoiceflow</span>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-1">
                            <a href="#features" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">Platform</a>
                            <a href="#how-it-works" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">Workflow</a>
                            <a href="#security" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">Security</a>
                        </div>

                        {/* Desktop Actions */}
                        <div className="hidden md:flex items-center space-x-3">
                            <Button
                                variant="ghost"
                                className="text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5"
                                onClick={() => navigate('/login')}
                            >
                                Sign In
                            </Button>
                            <Button
                                className="relative group bg-primary text-white overflow-hidden rounded-full px-6 transition-all hover:scale-105 shadow-[0_0_40px_-10px_rgba(99,102,241,0.8)]"
                                onClick={() => navigate('/signup')}
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    Start Free <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-primary via-indigo-400 to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            </Button>
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="md:hidden flex items-center">
                            <Button variant="ghost" size="icon" className="text-slate-300" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <div className="md:hidden absolute top-full left-0 w-full bg-[#030712]/95 backdrop-blur-xl border-b border-white/10 py-4 px-4 flex flex-col gap-2 shadow-2xl">
                        <a href="#features" className="px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white font-medium transition-all" onClick={() => setIsMenuOpen(false)}>Platform</a>
                        <a href="#how-it-works" className="px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white font-medium transition-all" onClick={() => setIsMenuOpen(false)}>Workflow</a>
                        <a href="#security" className="px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white font-medium transition-all" onClick={() => setIsMenuOpen(false)}>Security</a>
                        <div className="h-px bg-white/10 my-2" />
                        <Button variant="ghost" className="w-full justify-start px-4 py-6 text-slate-300 hover:text-white hover:bg-white/5 text-lg rounded-xl" onClick={() => navigate('/login')}>Sign In</Button>
                        <Button className="w-full justify-start px-4 py-6 bg-primary hover:bg-primary/90 text-white text-lg rounded-xl shadow-[0_0_20px_rgba(99,102,241,0.4)]" onClick={() => navigate('/signup')}>Start Free Trial</Button>
                    </div>
                )}
            </nav>

            <main className="relative z-10 pt-32 pb-16">
                {/* Hero Section */}
                <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col items-center text-center py-20 lg:py-32">
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm text-primary backdrop-blur-md mb-8 animate-fade-in ring-1 ring-inset ring-primary/20 shadow-[0_0_20px_rgba(99,102,241,0.2)]">
                        <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse shadow-[0_0_10px_rgba(99,102,241,1)]"></span>
                        <span className="font-medium tracking-wide">Enterprise-grade Security for Freelancers</span>
                    </div>

                    <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-8 animate-fade-in-delay leading-[1.1] text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-slate-400">
                        Invoicing with <br className="hidden md:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-indigo-400 to-purple-400 filter drop-shadow-[0_0_30px_rgba(99,102,241,0.4)]">Total Control.</span>
                    </h1>

                    <p className="text-lg md:text-xl text-slate-400 max-w-2xl mb-12 animate-fade-in-delay-2 leading-relaxed font-light">
                        Deploy secure, automated workflows. Create cryptographic-level precision invoices, manage end-to-end client communications, and accelerate your cash flow seamlessly.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-5 items-center justify-center animate-fade-in-delay-2 w-full sm:w-auto">
                        <Button
                            size="lg"
                            className="w-full sm:w-auto h-14 px-8 rounded-full bg-primary text-white font-medium hover:bg-primary/90 transition-all hover:scale-105 shadow-[0_0_40px_-5px_rgba(99,102,241,0.5)] border border-primary/50 relative overflow-hidden group"
                            onClick={() => navigate('/signup')}
                        >
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>
                            <span className="relative z-10 flex items-center gap-2 text-base">
                                Start Your Journey <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </Button>

                    </div>
                </section>

                {/* Dashboard Preview / Visualization */}
                <section id="security" className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto -mt-10 relative z-20">
                    <div className="rounded-2xl border border-white/10 bg-[#0a0f1d]/80 backdrop-blur-2xl p-2 sm:p-4 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] ring-1 ring-white/5 overflow-hidden group relative">
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>

                        {/* Fake Browser Chrome */}
                        <div className="flex items-center gap-2 mb-4 px-2">
                            <div className="w-3 h-3 rounded-full bg-slate-700/50"></div>
                            <div className="w-3 h-3 rounded-full bg-slate-700/50"></div>
                            <div className="w-3 h-3 rounded-full bg-slate-700/50"></div>
                        </div>

                        {/* Bento Grid Preview inside browser */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2 rounded-xl bg-white/5 border border-white/5 p-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
                                <div className="flex justify-between items-center mb-8 relative z-10">
                                    <div>
                                        <h3 className="text-xl font-semibold text-white mb-1">Revenue Flow</h3>
                                        <p className="text-sm text-slate-400">Monthly breakdown</p>
                                    </div>
                                    <h2 className="text-3xl font-bold text-white">$12,450<span className="text-sm font-normal text-primary ml-2">+14.5%</span></h2>
                                </div>
                                <div className="h-40 flex items-end gap-2 relative z-10">
                                    {[40, 70, 45, 90, 65, 110, 85].map((h, i) => (
                                        <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-primary/20 to-primary/80 hover:from-primary/40 hover:to-primary transition-colors cursor-pointer" style={{ height: `${h}%` }}></div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-xl bg-white/5 border border-white/5 p-6 flex flex-col justify-between">
                                <div>
                                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-4">
                                        <ShieldCheck className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-1">Status Active</h3>
                                    <p className="text-sm text-slate-400">All systems secure</p>
                                </div>
                                <div className="mt-8 space-y-3">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-slate-400">Encryption</span>
                                        <span className="text-white font-medium">AES-256</span>
                                    </div>
                                    <div className="w-full bg-white/10 rounded-full h-1.5"><div className="bg-emerald-400 h-1.5 rounded-full w-full"></div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Brands Section */}
                <section className="py-20 border-b border-white/5">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <p className="text-sm font-semibold text-slate-500 uppercase tracking-[0.2em] mb-8">Trusted by next-gen teams</p>
                        <div className="flex flex-wrap justify-center gap-x-12 gap-y-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
                            {['Stripe', 'Google', 'Spotify', 'Airbnb', 'Netflix'].map((brand) => (
                                <div key={brand} className="text-2xl md:text-3xl font-bold font-sans tracking-tighter text-white">
                                    {brand}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Cyber-style Features Bento */}
                <section id="features" className="py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-16">
                            <h2 className="text-primary font-medium tracking-widest text-sm mb-3 font-mono flex items-center gap-2">
                                <Zap className="w-4 h-4" /> ENGINE CAPABILITIES
                            </h2>
                            <h3 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight text-white">Advanced infrastructure<br />for autonomous billing.</h3>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
                            {/* Large Card */}
                            <div className="md:col-span-2 rounded-2xl bg-white/[0.02] border border-white/10 p-8 lg:p-12 hover:bg-white/[0.04] transition-colors relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity duration-500 group-hover:scale-110">
                                    <FileText className="w-32 h-32 text-primary" />
                                </div>
                                <div className="relative z-10 h-full flex flex-col justify-end max-w-md">
                                    <div className="w-12 h-12 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center text-primary mb-6 ring-1 ring-primary/50 shadow-[0_0_15px_rgba(99,102,241,0.3)]">
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <h4 className="text-2xl font-bold text-white mb-3">Cryptographic Precision Invoicing</h4>
                                    <p className="text-slate-400 leading-relaxed text-lg">
                                        Generate flawless, brand-aligned invoices instantly. Our intelligent builder anticipates your needs, minimizing friction while maximizing professional impact.
                                    </p>
                                </div>
                            </div>

                            {/* Small Card 1 */}
                            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-8 hover:bg-white/[0.04] transition-colors relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 blur-3xl rounded-full"></div>
                                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white mb-6">
                                    <Users className="w-6 h-6" />
                                </div>
                                <h4 className="text-xl font-bold text-white mb-3">Smart CRM Hub</h4>
                                <p className="text-slate-400 relative z-10">
                                    Centralize client intelligence. Access historical data, payment velocity, and outstanding balances in zero clicks.
                                </p>
                            </div>

                            {/* Small Card 2 */}
                            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-8 hover:bg-white/[0.04] transition-colors relative overflow-hidden">
                                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white mb-6">
                                    <Lock className="w-6 h-6" />
                                </div>
                                <h4 className="text-xl font-bold text-white mb-3">Zero-Trust Security</h4>
                                <p className="text-slate-400">
                                    Bank-grade encryption protects your financial data. Total privacy designed from the ground up.
                                </p>
                            </div>

                            {/* Medium Card */}
                            <div className="md:col-span-2 rounded-2xl bg-white/[0.02] border border-white/10 p-8 hover:bg-white/[0.04] transition-colors flex flex-col md:flex-row items-center gap-8 justify-between relative overflow-hidden group">
                                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                <div className="flex-1 relative z-10">
                                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white mb-6">
                                        <BarChart className="w-6 h-6" />
                                    </div>
                                    <h4 className="text-xl font-bold text-white mb-3">Deep Financial Analytics</h4>
                                    <p className="text-slate-400">
                                        Translate raw numbers into actionable intelligence. Monitor velocity, forecast revenue, and optimize pricing dynamically.
                                    </p>
                                </div>
                                <div className="w-full md:w-1/3 h-32 rounded-xl border border-white/10 bg-[#060a15] p-4 flex items-center justify-center relative">
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-24 h-24 rounded-full border-4 border-primary/20 border-t-primary animate-spin" style={{ animationDuration: '3s' }}></div>
                                    </div>
                                    <span className="relative z-10 text-white font-mono text-xl">99.9%</span>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {/* Workflow section with intense dark UI element */}
                <section id="how-it-works" className="py-24 relative">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-16 tracking-tight">Streamlined Execution Flow.</h2>

                        <div className="grid md:grid-cols-3 gap-8 relative max-w-4xl mx-auto">
                            <div className="hidden md:block absolute top-[28px] left-[20%] right-[20%] h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent -z-10"></div>

                            {[
                                { step: '01', title: 'Onboard Node', desc: 'Securely inject client parameters into the database securely.' },
                                { step: '02', title: 'Compile Invoice', desc: 'Generate line items & apply cryptographic signatures visually.' },
                                { step: '03', title: 'Execute Transfer', desc: 'Deploy payload directly to client with an encrypted payment gateway.' }
                            ].map((item, i) => (
                                <div key={i} className="flex flex-col items-center group cursor-default">
                                    <div className="w-14 h-14 rounded-full bg-[#0a0f1d] border border-white/10 flex items-center justify-center mb-6 ring-4 ring-[#030712] relative group-hover:border-primary transition-colors">
                                        <div className="absolute inset-0 rounded-full bg-primary/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <span className="text-lg font-mono text-slate-300 group-hover:text-white transition-colors relative z-10">{item.step}</span>
                                    </div>
                                    <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed max-w-xs">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Cyberpunk Footer CTA */}
                <section className="py-24">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-12 md:p-20 text-center relative overflow-hidden">
                            <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/20 blur-[100px] rounded-full pointer-events-none"></div>
                            <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-500/10 blur-[100px] rounded-full pointer-events-none"></div>

                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 relative z-10">Initialize Your Setup.</h2>
                            <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto relative z-10 font-light">
                                Stop losing time. Start commanding your billing cycle from a secure, unified interface today.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
                                <Button
                                    size="lg"
                                    className="h-14 px-10 text-base bg-white text-black hover:bg-slate-200 shadow-[0_0_30px_rgba(255,255,255,0.2)] rounded-full font-semibold transition-all hover:scale-105"
                                    onClick={() => navigate('/signup')}
                                >
                                    Create Free Account
                                </Button>

                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* Micro Footer */}
            <footer className="border-t border-white/10 bg-[#02040a] py-8 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between">
                    <div className="flex items-center gap-2 mb-4 md:mb-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#6366f1" className="w-6 h-6">
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                        </svg>
                        <span className="font-semibold text-white tracking-tight text-sm">Invoiceflow</span>
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                        STATUS: ONLINE | LATENCY: 12ms | VERSION: 2.4.0
                    </div>

                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
